import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { fmtTime, fmtDate, attendeeDayShort, attendeeDayLabel } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, MapPin, Shirt, Navigation, Info, Crown, Map, Users } from "lucide-react";

export default function Schedule() {
  const { eventId, attendeeId } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.schedule(eventId, attendeeId).then((d) => {
      if (active) { setData(d); setLoading(false); }
    });
    return () => { active = false; };
  }, [eventId, attendeeId]);

  const days = data?.days || [];
  const defaultDay = days.length ? String(days[0].day) : "1";

  return (
    <div className="pt-16">
      <div className="px-4 pt-4 pb-2">
        <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--pc-gold))]">President's Club 2026</span>
        <h1 className="font-serif text-4xl text-cream">My Schedule</h1>
        <p className="text-sm text-cream-dim mt-1">{data ? `${data.total} experiences curated for you` : "\u00a0"}</p>
      </div>

      {loading ? (
        <div className="px-4 space-y-3 mt-4">
          {[1, 2, 3].map((i) => (<div key={i} className="h-24 rounded-2xl bg-[hsl(var(--pc-surface))] animate-pulse" />))}
        </div>
      ) : (
        <Tabs value={days.some(d=>String(d.day)===defaultDay)?undefined:undefined} defaultValue={defaultDay} className="w-full">
          <div className="sticky top-0 z-20 att-bg px-4 pt-2 pb-3">
            <TabsList data-testid="attendee-schedule-day-tabs" className="w-full justify-start gap-2 bg-transparent p-0 h-auto overflow-x-auto no-scrollbar">
              {days.map((d) => (
                <TabsTrigger key={d.day} value={String(d.day)} className="flex-col items-center rounded-2xl border border-[hsl(var(--pc-line))] att-glass px-4 py-2 text-cream data-[state=active]:bg-[hsl(var(--pc-gold))] data-[state=active]:text-[hsl(var(--pc-ink))] shrink-0">
                  <span className="text-[10px] uppercase tracking-wider opacity-80">{attendeeDayShort(d.attendee_day) || "\u00A0"}</span>
                  <span className="text-sm font-semibold">{fmtDate(d.date).replace(",", "")}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {days.map((d) => (
            <TabsContent key={d.day} value={String(d.day)} className="mt-0 px-4 pb-6">
              <AnimatePresence mode="wait">
                <motion.div key={attendeeId + "-" + d.day} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="relative pl-4">
                  <div className="absolute left-[3px] top-2 bottom-2 w-px bg-[hsl(var(--pc-line))]" />
                  <div className="space-y-3">
                    {d.sessions.length === 0 ? (
                      <div data-testid="attendee-schedule-empty-day" className="att-card px-5 py-8 text-center">
                        <p className="text-cream/70 text-sm">No scheduled experiences for you today.</p>
                      </div>
                    ) : d.sessions.map((s) => (
                      <div key={s.id} className="relative">
                        <span className="absolute -left-4 top-5 h-2 w-2 rounded-full bg-[hsl(var(--pc-gold))] ring-4 ring-[hsl(var(--pc-ink))]" />
                        <SessionCard s={s} onClick={() => setSelected(s)} />
                      </div>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </TabsContent>
          ))}
        </Tabs>
      )}

      <SessionDetail session={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function SessionCard({ s, onClick }) {
  return (
    <button data-testid="attendee-schedule-session-card" onClick={onClick} className="w-full text-left att-card overflow-hidden flex">
      <div className="relative w-24 shrink-0"><img src={s.image} alt="" className="absolute inset-0 h-full w-full object-cover" /></div>
      <div className="flex-1 p-3 min-w-0">
        <div className="flex items-center gap-2 text-xs font-semibold text-[hsl(var(--pc-gold))]">
          <Clock className="h-3 w-3" />{s.all_day ? "All day" : `${fmtTime(s.start_time)} – ${fmtTime(s.end_time)}`}
          {!s.is_general && <Crown className="h-3 w-3" />}
        </div>
        <div className="font-serif text-lg leading-tight text-cream mt-0.5 truncate">{s.title}</div>
        {s.venue && <div className="text-xs text-cream-dim flex items-center gap-1 mt-1 truncate"><MapPin className="h-3 w-3 shrink-0" /> {s.venue}</div>}
      </div>
    </button>
  );
}

function SessionDetail({ session, onClose }) {
  return (
    <Sheet open={!!session} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl border-none p-0 max-h-[92vh] overflow-y-auto no-scrollbar att-bg">
        {session && (
          <div className="mx-auto w-full max-w-[30rem]">
            <div className="relative h-52 w-full">
              <img src={session.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 att-hero-fade" />
              <div className="absolute bottom-4 left-5 right-5">
                <div className="flex items-center gap-2 text-xs font-semibold text-cream mb-1">
                  <Clock className="h-3.5 w-3.5" />{session.all_day ? "All day" : `${fmtTime(session.start_time)} – ${fmtTime(session.end_time)}`}
                  <span className="opacity-80">· {attendeeDayLabel(session.attendee_day, session.date)}</span>
                </div>
                <h2 className="font-serif text-3xl leading-tight text-cream">{session.title}</h2>
              </div>
            </div>
            <div className="p-5 space-y-5">
              {!session.is_general && (
                <div className="flex flex-wrap items-center gap-2 rounded-xl bg-[hsl(var(--pc-gold)/0.12)] border border-[hsl(var(--pc-gold)/0.35)] px-3 py-2">
                  <Crown className="h-4 w-4 text-[hsl(var(--pc-gold))]" />
                  <span className="text-xs font-semibold text-cream">Exclusive experience</span>
                </div>
              )}
              {session.description && <p className="text-sm text-cream/90 leading-relaxed">{session.description}</p>}
              <div className="grid grid-cols-2 gap-3">
                <DetailItem icon={MapPin} label="Venue" value={session.venue} />
                <DetailItem icon={Navigation} label="Meeting point" value={session.meeting_point} />
                <DetailItem icon={Shirt} label="Dress code" value={session.dress_code} />
                <DetailItem icon={Users} label="Guests" value={session.guest_policy || (session.plus_one_eligible ? "Plus-ones welcome" : "Attendee only")} />
              </div>
              {session.notes && (
                <div className="flex items-start gap-2 rounded-xl att-glass p-3">
                  <Info className="h-4 w-4 mt-0.5 text-[hsl(var(--pc-gold))] shrink-0" />
                  <div><div className="text-xs font-semibold text-cream">Good to know</div><div className="text-sm text-cream-dim">{session.notes}</div></div>
                </div>
              )}
              <div className="rounded-2xl overflow-hidden border border-[hsl(var(--pc-line))]">
                <div className="relative h-32 att-glass grid place-items-center">
                  <div className="text-center text-cream-dim"><Map className="h-6 w-6 mx-auto mb-1" /><div className="text-xs font-semibold text-cream">Map & directions</div><div className="text-[11px]">Interactive resort map coming soon</div></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DetailItem({ icon: Icon, label, value }) {
  if (value == null || String(value).trim() === "") return null;
  return (
    <div className="rounded-xl att-glass p-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold text-cream-dim"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="text-sm font-medium text-cream mt-1">{value}</div>
    </div>
  );
}
